<template>
    <div class="w-full" dusk="chunk-uploader">
        <jet-secondary-button dusk="upload-button" v-show="!uploading" ref="uploadButton" class="flex-shrink" type="button" @click.native="selectFile">
            <div class="flex items-center w-full space-x-2">
                <svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span class="font-semibold">Upload Video</span>
            </div>
        </jet-secondary-button>
        <transition name="fade">
            <div v-if="uploading" class="flex flex-col items-center w-full lg:flex-row lg:space-x-4">
                <div class="w-full h-56 rounded shadow overflow-hidden lg:w-1/3">
                    <img :src="videoPreview" ref="videoPreview" class="align-middle w-full h-56 rounded object-cover" alt="Video Preview" />
                </div>

                <div class="flex flex-col flex-grow items-start mt-2 w-full space-y-2 lg:mt-0 lg:w-auto">
                    <div class="w-full">
                        <button v-if="!fileUploaded" @click="cancelUpload" type="button" class="flex items-center justify-end w-full hover:text-red-400 text-red-600 space-x-1">
                            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            <span class="text-xs font-semibold uppercase">cancel</span>
                        </button>
                        <button v-if="fileUploaded" @click="cancelUpload" type="button" class="flex items-center justify-end w-full hover:text-red-400 text-red-600 space-x-1">
                            <span class="text-xs font-semibold uppercase">delete video</span>
                        </button>
                        <div class="relative py-1">
                            <div class="flex h-3 text-xs bg-purple-200 rounded overflow-hidden">
                                <div :style="progressWidth" :class="[fileUploaded ? 'bg-green-500' : 'bg-purple-500']" class="flex flex-col justify-center text-center text-white whitespace-nowrap shadow-none"></div>
                            </div>
                        </div>
                        <div class="flex justify-between space-x-3">
                            <span class="flex items-center text-gray-700 text-xs overflow-hidden lg:text-sm">
                                <span class="truncate">{{ file.name }}</span>
                            </span>
                            <span :class="[fileUploaded ? 'text-green-700' : 'text-purple-700']" class="flex-shrink-0 text-xs uppercase lg:text-sm hidden">{{ progress }}%</span>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
        <input name="video" class="hidden opacity-0" ref="fileInput" type="file" @change="select" accept=".mov,.mp4,.avi,.3gp,.webm,.mkv,.wmv,.ogv,.mpeg,.mpg" />
        <div v-if="thumbnailError" class="mt-4 text-red-600 p-2 border border-red-600 rounded">{{ thumbnailError }}</div>
    </div>
</template>

<script>
import JetSecondaryButton from "@/Jetstream/SecondaryButton"

export default {
    components: { JetSecondaryButton },
    props: {
        post: {
            type: Object,
            required: true,
        },
    },

    watch: {
        chunks(n, o) {
            if (n.length > 0 && !this.cancel) {
                this.upload()
            }
        },
        file(val) {
            if (val) {
                this.$refs.uploadButton.blur()
            } else {
                this.$emit("cancelled")
            }
        },
        videoPreview(str) {
            this.fileHasBeenLoaded()
            this.$emit("videoPreview", str)
        },
        fileUploaded(value) {
            if (value) {
                this.$emit("uploaded")
            }
        },
        uploading(bool) {
            if (bool) {
                this.$emit("uploading", true)
            } else {
                this.$emit("uploading", false)
            }
        },
    },

    computed: {
        progressWidth() {
            if (!this.progress) return null
            return { width: this.progress + "%" }
        },
        progress() {
            if (!this.file) return null
            let progress = Math.floor((this.uploaded * 100) / this.file.size)
            return progress <= 100 ? progress : 100
        },
        formData() {
            let formData = new FormData()
            formData.set("is_last", this.chunks.length === 1)
            if (this.chunks.length && this.file) {
                formData.set("file", this.chunks[0], `${this.file.name}.part`)
            }
            return formData
        },
        config() {
            return {
                method: "POST",
                data: this.formData,
                url: this.route("posts.uploadVideo.store", { post: this.post, competitor: this.competitor.id }),
                headers: {
                    "Content-Type": "application/octet-stream",
                },
                onUploadProgress: (event) => {
                    this.uploaded += event.loaded
                },
            }
        },
        uploading() {
            return this.videoPreview && this.file
        },
    },

    data() {
        return {
            file: null,
            chunks: [],
            uploaded: 0,
            videoPreview: null,
            cancel: false,
            fileUploaded: false,
            competitor: this.$page.props.competitor,
            thumbnailError: null,
        }
    },

    methods: {
        selectFile() {
            console.log("Video selected")
            this.$refs.fileInput.value = ""
            this.$refs.fileInput.click()
        },
        async select(event) {
            try {
                console.log("File selected, checking resolution...")
                const resolutionValid = await this.checkResolution(event)
                if (!resolutionValid) {
                    this.thumbnailError = "The video resolution is too low. Please upload an HD video with a minimum resolution of 1280x720 pixels."
                    this.file = null
                    return
                }
                await this.getVideoThumbnail(event)
                this.file = event.target.files.item(0)
                this.createChunks()
                this.thumbnailError = null // Clear any previous errors
            } catch (error) {
                console.error("Error processing video:", error)
                this.thumbnailError = error.message || "An unexpected error occurred during video processing. Please try again."
                this.file = null
            }
        },
        upload() {
            axios(this.config)
                .then((response) => {
                    this.chunks.shift()
                    this.fileUploaded = response.data.uploaded && response.data.isLast
                })
                .catch((error) => console.log(error.response))
        },
        cancelUpload() {
            this.cancel = true
            this.$nextTick(() => {
                this.file = null
                this.chunks = []
                this.uploaded = 0
                this.videoPreview = null
                this.cancel = false
                this.fileUploaded = false
            })
        },
        createChunks() {
            let size = 1024 * 1024 * 10 // 10MB
            this.chunks = []
            if (this.file.size <= size) {
                this.chunks.push(this.file)
            } else {
                let chunks = Math.ceil(this.file.size / size)
                for (let i = 0; i < chunks; i++) {
                    this.chunks.push(this.file.slice(i * size, Math.min(i * size + size, this.file.size), this.file.type))
                }
            }
        },
        checkResolution(event) {
            return new Promise((resolve, reject) => {
                const file = event.target.files[0]
                const fileReader = new FileReader()
                fileReader.onload = () => {
                    const blob = new Blob([fileReader.result], { type: file.type })
                    const url = URL.createObjectURL(blob)
                    const video = document.createElement("video")
                    video.addEventListener("loadeddata", () => {
                        console.log(`Video resolution: ${video.videoWidth}x${video.videoHeight}`)
                        const isResolutionValid = video.videoWidth >= 1280 && video.videoHeight >= 720
                        URL.revokeObjectURL(url)
                        if (isResolutionValid) {
                            resolve(true)
                        } else {
                            reject(new Error("The video resolution is too low. Please upload an HD video with a minimum resolution of 1280x720 pixels."))
                        }
                    })
                    video.addEventListener("error", () => {
                        URL.revokeObjectURL(url)
                        reject(new Error("Failed to load video for resolution check"))
                    })
                    video.preload = "metadata"
                    video.src = url
                }
                fileReader.onerror = () => reject(new Error("Error reading file for resolution check"))
                fileReader.readAsArrayBuffer(file)
            })
        },
        getVideoThumbnail(event) {
            return new Promise((resolve, reject) => {
                const file = event.target.files[0]
                if (!file) {
                    reject(new Error("No file selected"))
                    return
                }

                const fileReader = new FileReader()

                fileReader.onload = () => {
                    const blob = new Blob([fileReader.result], { type: file.type })
                    const url = URL.createObjectURL(blob)
                    const video = document.createElement("video")

                    video.addEventListener("loadeddata", () => {
                        console.log("Video metadata loaded, extracting frame...")
                        this.extractFrame(video, url, resolve, reject)
                    })

                    video.addEventListener("error", (e) => {
                        console.error("Video error:", e)
                        URL.revokeObjectURL(url)
                        reject(new Error("Failed to load video"))
                    })

                    video.preload = "metadata"
                    video.src = url
                    video.muted = true
                    video.playsInline = true
                    video.play()
                }

                fileReader.onerror = () => {
                    reject(new Error("Error reading file"))
                }

                fileReader.readAsArrayBuffer(file)
            })
        },
        extractFrame(video, url, resolve, reject) {
            const canvas = document.createElement("canvas")
            canvas.width = video.videoWidth
            canvas.height = video.videoHeight
            const ctx = canvas.getContext("2d")

            try {
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
                const image = canvas.toDataURL()
                const success = image.length > 100000

                if (success) {
                    console.log("Frame extracted successfully.")
                    this.videoPreview = image
                    URL.revokeObjectURL(url)
                    resolve(image)
                } else {
                    console.error("Frame extraction failed, image too small.")
                    URL.revokeObjectURL(url)
                    reject(new Error("Failed to extract frame"))
                }
            } catch (e) {
                console.error("Error extracting frame:", e)
                URL.revokeObjectURL(url)
                reject(new Error("Error extracting frame"))
            }
        },
        fileHasBeenLoaded: _.debounce(function () {
            this.$emit("selected")
        }, 300),
    },
}
</script>

<!--<template>-->
<!--    <div class="w-full" dusk="chunk-uploader">-->
<!--        <jet-secondary-button dusk="upload-button" v-show="!uploading" ref="uploadButton" class="flex-shrink" type="button" @click.native="selectFile">-->
<!--            <div class="flex items-center w-full space-x-2">-->
<!--                <svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">-->
<!--                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />-->
<!--                </svg>-->
<!--                <span class="font-semibold">Upload Video</span>-->
<!--            </div>-->
<!--        </jet-secondary-button>-->
<!--        <transition name="fade">-->
<!--            <div v-if="uploading" class="flex flex-col items-center w-full lg:flex-row lg:space-x-4">-->
<!--                <div class="w-full h-56 rounded shadow overflow-hidden lg:w-1/3">-->
<!--                    <img :src="videoPreview" ref="videoPreview" class="align-middle w-full h-56 rounded object-cover" alt="Video Preview" />-->
<!--                </div>-->

<!--                <div class="flex flex-col flex-grow items-start mt-2 w-full space-y-2 lg:mt-0 lg:w-auto">-->
<!--                    <div class="w-full">-->
<!--                        <button v-if="!fileUploaded" @click="cancelUpload" type="button" class="flex items-center justify-end w-full hover:text-red-400 text-red-600 space-x-1">-->
<!--                            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">-->
<!--                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />-->
<!--                            </svg>-->
<!--                            <span class="text-xs font-semibold uppercase">cancel</span>-->
<!--                        </button>-->
<!--                        <button v-if="fileUploaded" @click="cancelUpload" type="button" class="flex items-center justify-end w-full hover:text-red-400 text-red-600 space-x-1">-->
<!--                            <span class="text-xs font-semibold uppercase">delete video</span>-->
<!--                        </button>-->
<!--                        <div class="relative py-1">-->
<!--                            <div class="flex h-3 text-xs bg-purple-200 rounded overflow-hidden">-->
<!--                                <div :style="progressWidth" :class="[fileUploaded ? 'bg-green-500' : 'bg-purple-500']" class="flex flex-col justify-center text-center text-white whitespace-nowrap shadow-none"></div>-->
<!--                            </div>-->
<!--                        </div>-->
<!--                        <div class="flex justify-between space-x-3">-->
<!--                            <span class="flex items-center text-gray-700 text-xs overflow-hidden lg:text-sm">-->
<!--                                <span class="truncate">{{ file.name }}</span>-->
<!--                            </span>-->
<!--                            <span :class="[fileUploaded ? 'text-green-700' : 'text-purple-700']" class="flex-shrink-0 text-xs uppercase lg:text-sm hidden">{{ progress }}%</span>-->
<!--                        </div>-->
<!--                    </div>-->
<!--                </div>-->
<!--            </div>-->
<!--        </transition>-->
<!--        <input name="video" class="hidden opacity-0" ref="fileInput" type="file" @change="select" accept=".mov,.mp4,.avi,.3gp,.webm,.mkv,.wmv,.ogv,.mpeg,.mpg" />-->
<!--    </div>-->
<!--</template>-->

<!--<script>-->
<!--import JetSecondaryButton from "@/Jetstream/SecondaryButton"-->

<!--export default {-->
<!--    components: { JetSecondaryButton },-->
<!--    props: {-->
<!--        post: {-->
<!--            type: Object,-->
<!--            required: true,-->
<!--        },-->
<!--    },-->

<!--    watch: {-->
<!--        chunks(n, o) {-->
<!--            if (n.length > 0 && !this.cancel) {-->
<!--                this.upload()-->
<!--            }-->
<!--        },-->
<!--        file(val) {-->
<!--            if (val) {-->
<!--                this.$refs.uploadButton.blur()-->
<!--            } else {-->
<!--                this.$emit("cancelled")-->
<!--            }-->
<!--        },-->
<!--        videoPreview(str) {-->
<!--            this.fileHasBeenLoaded()-->
<!--            this.$emit("videoPreview", str)-->
<!--        },-->
<!--        fileUploaded(value) {-->
<!--            if (value) {-->
<!--                this.$emit("uploaded")-->
<!--            }-->
<!--        },-->
<!--        uploading(bool) {-->
<!--            if (bool) {-->
<!--                this.$emit("uploading", true)-->
<!--            } else {-->
<!--                this.$emit("uploading", false)-->
<!--            }-->
<!--        },-->
<!--    },-->

<!--    computed: {-->
<!--        progressWidth() {-->
<!--            if (!this.progress) return null-->
<!--            return { width: this.progress + "%" }-->
<!--        },-->
<!--        progress() {-->
<!--            if (!this.file) return null-->
<!--            let progress = Math.floor((this.uploaded * 100) / this.file.size)-->
<!--            return progress <= 100 ? progress : 100-->
<!--        },-->
<!--        formData() {-->
<!--            let formData = new FormData()-->
<!--            formData.set("is_last", this.chunks.length === 1)-->
<!--            if (this.chunks.length && this.file) {-->
<!--                formData.set("file", this.chunks[0], `${this.file.name}.part`)-->
<!--            }-->
<!--            return formData-->
<!--        },-->
<!--        config() {-->
<!--            return {-->
<!--                method: "POST",-->
<!--                data: this.formData,-->
<!--                url: this.route("posts.uploadVideo.store", { post: this.post, competitor: this.competitor.id }),-->
<!--                headers: {-->
<!--                    "Content-Type": "application/octet-stream",-->
<!--                },-->
<!--                onUploadProgress: (event) => {-->
<!--                    this.uploaded += event.loaded-->
<!--                },-->
<!--            }-->
<!--        },-->
<!--        uploading() {-->
<!--            return this.videoPreview && this.file-->
<!--        },-->
<!--    },-->

<!--    data() {-->
<!--        return {-->
<!--            file: null,-->
<!--            chunks: [],-->
<!--            uploaded: 0,-->
<!--            videoPreview: null,-->
<!--            cancel: false,-->
<!--            fileUploaded: false,-->
<!--            competitor: this.$page.props.competitor,-->
<!--        }-->
<!--    },-->

<!--    methods: {-->
<!--        selectFile() {-->
<!--            console.log("Video selected")-->
<!--            this.$refs.fileInput.value = ""-->
<!--            this.$refs.fileInput.click()-->
<!--        },-->
<!--        async select(event) {-->
<!--            try {-->
<!--                await this.getVideoThumbnail(event) // Wait for thumbnail extraction to complete-->
<!--                this.file = event.target.files.item(0) // This line runs after the thumbnail has been extracted-->
<!--                this.createChunks()-->
<!--            } catch (error) {-->
<!--                console.error("Error extracting thumbnail:", error)-->
<!--                // Handle any errors (e.g., file not selected, unable to extract thumbnail)-->
<!--            }-->
<!--        },-->
<!--        upload() {-->
<!--            axios(this.config)-->
<!--                .then((response) => {-->
<!--                    this.chunks.shift()-->
<!--                    this.fileUploaded = response.data.uploaded && response.data.isLast-->
<!--                })-->
<!--                .catch((error) => console.log(error.response))-->
<!--        },-->
<!--        cancelUpload() {-->
<!--            this.cancel = true-->
<!--            this.$nextTick(() => {-->
<!--                this.file = null-->
<!--                this.chunks = []-->
<!--                this.uploaded = 0-->
<!--                this.videoPreview = null-->
<!--                this.cancel = false-->
<!--                this.fileUploaded = false-->
<!--            })-->
<!--        },-->
<!--        createChunks() {-->
<!--            let size = 1024 * 1024 * 10, // 10MB-->
<!--                chunks = Math.ceil(this.file.size / size)-->

<!--            for (let i = 0; i < chunks; i++) {-->
<!--                this.chunks.push(this.file.slice(i * size, Math.min(i * size + size, this.file.size), this.file.type))-->
<!--            }-->
<!--        },-->
<!--        // Note: This method assumes `event` is the input event containing the selected video file.-->
<!--        getVideoThumbnail(event) {-->
<!--            return new Promise((resolve, reject) => {-->
<!--                var file = event.target.files[0]-->
<!--                if (!file) {-->
<!--                    reject("No file selected")-->
<!--                    return-->
<!--                }-->

<!--                var fileReader = new FileReader()-->

<!--                fileReader.onload = () => {-->
<!--                    let blob = new Blob([fileReader.result], { type: file.type })-->
<!--                    let url = URL.createObjectURL(blob)-->
<!--                    let video = document.createElement("video")-->

<!--                    let timeupdate = () => {-->
<!--                        if (snapImage()) {-->
<!--                            video.removeEventListener("timeupdate", timeupdate)-->
<!--                            video.pause()-->
<!--                        }-->
<!--                    }-->

<!--                    video.addEventListener("loadeddata", () => {-->
<!--                        if (snapImage()) {-->
<!--                            video.removeEventListener("timeupdate", timeupdate)-->
<!--                        }-->
<!--                    })-->

<!--                    video.addEventListener("timeupdate", timeupdate)-->
<!--                    video.preload = "metadata"-->
<!--                    video.src = url-->
<!--                    video.muted = true-->
<!--                    video.playsInline = true-->
<!--                    video.play()-->

<!--                    const snapImage = () => {-->
<!--                        var canvas = document.createElement("canvas")-->
<!--                        canvas.width = video.videoWidth-->
<!--                        canvas.height = video.videoHeight-->
<!--                        canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height)-->
<!--                        var image = canvas.toDataURL()-->
<!--                        var success = image.length > 100000-->
<!--                        if (success) {-->
<!--                            this.videoPreview = image-->
<!--                            URL.revokeObjectURL(url)-->
<!--                            resolve(image)-->
<!--                        }-->
<!--                        return success-->
<!--                    }-->
<!--                }-->

<!--                // For images-->
<!--                if (file.type.match("image.*")) {-->
<!--                    fileReader.onload = (e) => {-->
<!--                        this.videoPreview = e.target.result-->
<!--                        resolve(e.target.result)-->
<!--                    }-->
<!--                    fileReader.readAsDataURL(file)-->
<!--                } else {-->
<!--                    fileReader.readAsArrayBuffer(file)-->
<!--                }-->
<!--            })-->
<!--        },-->
<!--        // getVideoThumbnail(event) {-->
<!--        //     var file = event.target.files[0]-->
<!--        //     var fileReader = new FileReader()-->
<!--        //     if (file.type.match("image")) {-->
<!--        //         fileReader.onload = () => {-->
<!--        //             this.videoPreview = fileReader.result-->
<!--        //         }-->
<!--        //         fileReader.readAsDataURL(file)-->
<!--        //     } else {-->
<!--        //         fileReader.onload = () => {-->
<!--        //             let blob = new Blob([fileReader.result], { type: file.type })-->
<!--        //             let url = URL.createObjectURL(blob)-->
<!--        //             let video = document.createElement("video")-->
<!--        //             let timeupdate = () => {-->
<!--        //                 if (this.snapImage(video, url)) {-->
<!--        //                     video.removeEventListener("timeupdate", timeupdate)-->
<!--        //                     video.pause()-->
<!--        //                 }-->
<!--        //             }-->
<!--        //             video.addEventListener("loadeddata", () => {-->
<!--        //                 if (this.snapImage(video, url)) {-->
<!--        //                     video.removeEventListener("timeupdate", timeupdate)-->
<!--        //                 }-->
<!--        //             })-->
<!--        //-->
<!--        //             video.addEventListener("timeupdate", timeupdate)-->
<!--        //             video.preload = "metadata"-->
<!--        //             video.src = url-->
<!--        //             // Load video in Safari / IE11-->
<!--        //             video.muted = true-->
<!--        //             video.playsInline = true-->
<!--        //             video.play()-->
<!--        //         }-->
<!--        //         fileReader.readAsArrayBuffer(file)-->
<!--        //     }-->
<!--        // },-->
<!--        // snapImage(video, url) {-->
<!--        //     var scaleFactor = 0.25 // Scale the thumbnail to 25% of the original video size-->
<!--        //     var width = video.videoWidth * scaleFactor-->
<!--        //     var height = video.videoHeight * scaleFactor-->
<!--        //-->
<!--        //     var canvas = document.createElement("canvas")-->
<!--        //     canvas.width = width-->
<!--        //     canvas.height = height-->
<!--        //     var ctx = canvas.getContext("2d")-->
<!--        //-->
<!--        //     // Use drawImage() to scale the video frame-->
<!--        //     ctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight, 0, 0, width, height)-->
<!--        //-->
<!--        //     var image = canvas.toDataURL()-->
<!--        //     var success = image.length > 100000-->
<!--        //     if (success) {-->
<!--        //         this.videoPreview = image-->
<!--        //         URL.revokeObjectURL(url) // Releasing the blob URL-->
<!--        //     }-->
<!--        //     return success-->
<!--        // },-->

<!--        fileHasBeenLoaded: _.debounce(function () {-->
<!--            this.$emit("selected")-->
<!--        }, 300),-->
<!--    },-->
<!--}-->
<!--</script>-->
